<template>
	<div class="controls">
		<div class="controls__actions l-button-group">
			<!-- STATUS: INITIAL -->
			<Button
				v-if="currentUiStatus === UI_STATUS.INITIAL"
				:text="$I18n.trans('event.multiformat_editor.labels.ready')"
				theme="dark"
				modifier="primary"
				@click="$emit('setReadyStatus')"
			/>
			<Button
				v-if="[UI_STATUS.READY, UI_STATUS.RECORDING].includes(currentUiStatus)"
				theme="dark"
				:text="$I18n.trans('event.multiformat_editor.labels.stop')"
				:disabled="currentUiStatus === UI_STATUS.READY"
				@click="$emit('stopClipRecording')"
			/>
			<Button
				v-if="[UI_STATUS.READY, UI_STATUS.RECORDING].includes(currentUiStatus)"
				theme="dark"
				modifier="danger"
				:text="$Helpers.ucfirst($I18n.trans('commons.cancel'))"
				@click="$emit('cancelReadyStatus')"
			/>
			<Button
				v-if="[UI_STATUS.FINISHED_RECORDING, UI_STATUS.GENERATING_VIDEO_CROP, UI_STATUS.VIDEO_CROP_AVAILABLE].includes(currentUiStatus)"
				theme="dark"
				modifier="secondary"
				:text="$I18n.trans('commons.preview')"
				@click="$emit('previewClip')"
			/>
			<Button
				v-if="[UI_STATUS.FINISHED_RECORDING, UI_STATUS.GENERATING_VIDEO_CROP, UI_STATUS.VIDEO_CROP_AVAILABLE].includes(currentUiStatus)"
				theme="dark"
				modifier="danger"
				:text="$I18n.trans('event.multiformat_editor.labels.restart')"
				@click="$emit('resetClipping')"
			/>
			<Button
				v-if="currentUiStatus === UI_STATUS.PREVIEW"
				theme="dark"
				modifier="secondary"
				:text="$Helpers.ucfirst($I18n.trans('commons.cancel'))"
				@click="$emit('stopPreview')"
			/>
			<!-- STATUS: VIDEO_CROP_FAILURE -->
			<Button
				v-if="currentUiStatus === UI_STATUS.VIDEO_CROP_FAILURE"
				theme="dark"
				:text="$I18n.trans('event.multiformat_editor.labels.restart')"
				@click="$emit('resetClipping')"
			/>
		</div>
		<div class="controls__spacer" />
		<div
			v-if="!isCommunityCropping && [UI_STATUS.FINISHED_RECORDING, UI_STATUS.GENERATING_VIDEO_CROP, UI_STATUS.VIDEO_CROP_AVAILABLE].includes(currentUiStatus)"
			class="controls__graphic-settings"
		>
			<Select
				name="graphic"
				class="controls__graphic-settings-select"
				:searchable="false"
				:clearable="false"
				:value="selectedGraphicId"
				:options="graphicOptions"
				:dark-theme="!$arenaConfig.isLightTheme"
				@change="$emit('setSelectedGraphicId', $event)"
			/>
		</div>
		<div class="controls__infos l-button-group">
			<div
				v-if="currentUiStatus === UI_STATUS.RECORDING"
				class="controls__timer"
			>
				<div class="controls__timer-count">
					{{ remainingClipTimeSecsLeadingZero }}
				</div>
				<div class="controls__timer-label">
					{{ $I18n.trans_choice('commons.second', remainingClipTimeSecs) }}
				</div>
			</div>
			<div
				v-if="currentUiStatus === UI_STATUS.PREVIEW"
				class="controls__timer"
			>
				<div class="controls__timer-count">
					{{ previewCountdownLeadingZero }}
				</div>
				<div class="controls__timer-label">
					{{ $I18n.trans_choice('commons.second', previewCountdown) }}
				</div>
			</div>
			<div
				v-if="maxDurationReachedInfoVisible && currentUiStatus === UI_STATUS.FINISHED_RECORDING"
				class="controls__info"
			>
				{{ $I18n.trans('event.multiformat_editor.labels.30s_limit_reached') }}
			</div>
			<!-- STATUS: READY -->
			<div
				v-if="currentUiStatus === UI_STATUS.READY"
				class="controls__info"
			>
				{{ $I18n.trans('event.multiformat_editor.labels.drag_to_start') }}
			</div>
			<template v-if="[UI_STATUS.FINISHED_RECORDING, UI_STATUS.GENERATING_VIDEO_CROP, UI_STATUS.VIDEO_CROP_AVAILABLE].includes(currentUiStatus)">
				<Button
					:text="$I18n.trans('commons.type_create', {'type': $I18n.trans_choice('commons.video', 1)})"
					theme="dark"
					@click="$emit('createVideoCrop')"
				/>
			</template>
		</div>
	</div>
</template>

<script>
import Button from '@shared/components/Button';
import Select from '@shared/components/Select';

import { UI_STATUS } from './MultiFormatTool';

export default {
	name:       'MultiFormatControls',
	components: { Button, Select },
	props:      {
		currentUiStatus: {
			type:     String,
			required: true,
		},
		selectedGraphicId: {
			type:    [String, Number],
			default: null,
		},
		graphicOptions: {
			type:    Array,
			default: () => [],
		},
		maxDurationReachedInfoVisible: {
			type:    Boolean,
			default: false,
		},
		remainingClipTimeSecs: {
			type:    Number,
			default: 0,
		},
		remainingClipTimeSecsLeadingZero: {
			type:    String,
			default: '',
		},
		previewCountdown: {
			type:    Number,
			default: 0,
		},
		previewCountdownLeadingZero: {
			type:    String,
			default: '',
		},
		isCommunityCropping: {
			type:    Boolean,
			default: false,
		},
	},
	data() {
		return {
			UI_STATUS,
		};
	},
};
</script>

<style lang="scss" scoped>
@import '@shared/sass/shared-variables';

.controls {
	max-width: 1200px;
	margin: 0 auto;
	display: flex;
	justify-content: space-between;
	align-items: center;
	gap: $sp5;
	flex-grow: 1;

	@include mobile-portrait {
		flex-direction: column;
		align-items: flex-start;
	}
}

.controls__actions {
	display: flex;

	@include mobile-portrait {
		width: 100%;
	}
}

.controls__infos {
	display: flex;
	gap: $sp4;

	@include mobile-portrait {
		width: 100%;
	}
}

.controls__button-group {
	display: flex;
	gap: $sp4;

	> * {
		flex-grow: 1;
		flex-basis: 50%;
	}

	@include mobile-landscape {
		flex-wrap: wrap;
		gap: $sp3;
		padding: 0 $sp2;
	}
}

.controls__info {
	display: flex;
	align-items: center;
}

.controls__timer {
	color: var(--color-grey-300);
	text-align: right;

	@include mobile-portrait {
		width: 100%;
		text-align: center;
	}
}

.controls__timer-count {
	font-size: 67px; // stylelint-disable-line
	line-height: 1; // stylelint-disable-line
	font-weight: 600; // stylelint-disable-line
}

.controls__timer-label {
	padding-right: 0.2em;
}

// .controls__preview-info-label {}

.controls__preview-info-value {
	@include font-size(fs-150);
}

.controls__graphic-settings {
	@include mobile-portrait {
		width: 100%;
	}
}

.controls__graphic-settings-select {
	background-color: $form-color-selection-active--dark;
	min-width: 240px;
	margin-bottom: 0;

	@include mobile-portrait {
		background-color: transparent;
	}
}

.controls__spacer {
	display: none;

	@include size(md-up) {
		display: block;
		flex-grow: 1;
	}
}
</style>
